import { useCallback, useEffect, useRef, useState } from "react";

import {
  type ProxyRequestMessage,
  type ProxyResponseMessage,
  type ProxyRequestState,
  type ProxyRequest,
} from "./ProxyTypes";
import { formatRequestText, formatResponseText, parseDateHeader, parseRequestText } from "./format";

import { setHeaderValue } from "../util";

import { ClientSessionState } from "../SandboxContext";

/**
 * Hook for managing the man-in-the-middle proxy used in some sandboxes.
 * The sandbox's 'proxy' container allows requests to be intercepted and modified
 * by the client before they reach the destination app.
 *
 * core/proxy does not have a strictly-defined API specification - here's a de facto
 * summary of its API:
 *
 * - GET /proxy/intercept - opens an event stream that sends a message for each request
 *   and response that pass through the proxy.
 *
 * - PUT /proxy/intercept - toggles intercepting of requests on. Any new requests that
 *   pass through the proxy will be held until the client calls POST /proxy/resume or
 *   toggles intercepts off.
 *
 * - DELETE /proxy/intercept - toggles intercepting of requests off. If any requests are
 *   currently being held, they will be sent along without modification.
 *
 * - POST /proxy/resume - Allows the currently-intercepted request to be sent to its
 *   destination, optionally with user modifications applied.
 *
 * Quirks of the core/proxy implementation:
 *
 * - When a sandbox first starts up, the proxy is in an indeterminate state and needs to
 *   be explicitly told whether to enable or disable intercepts.
 *
 * - The GET /proxy/intercept event stream is used to populate the proxy history when
 *   connecting to an existing sandbox (after a page reload etc.), but it does not
 *   reliably replay the full history.
 */

export interface UseProxyResult {
  isIntercepting: boolean;
  requests: ProxyRequest[];
  pendingRequestText: string | null;
  setInterceptState: (isIntercepting: boolean) => Promise<boolean>;
  resumeInterceptedRequest: (requestText: string) => Promise<boolean>;
}

export const useProxyInternal = (
  sessionStatus: ClientSessionState["overall"],
  url: string | null,
  onError: (err: unknown) => void
): UseProxyResult => {
  const [isIntercepting, setIsIntercepting] = useState(false);
  const [requests, setRequests] = useState<ProxyRequest[]>([]);
  const [pendingRequestText, setPendingRequestText] = useState<string | null>(null);

  const sourceRef = useRef<EventSource | null>(null);
  const requestStatesByRequestIdRef = useRef<{ [key: string]: ProxyRequestState }>({});
  const pendingRequestIdRef = useRef("");
  const initializedForUrlRef = useRef<string | null>(null);

  /**
   * Handle incoming request messages
   */
  const handleRequestMessage = useCallback(
    (ev: Event) => {
      const messageEvent = ev as MessageEvent;
      const req = JSON.parse(messageEvent.data) as ProxyRequestMessage;

      if (requestStatesByRequestIdRef.current[req.request_id] !== undefined) {
        return;
      }

      requestStatesByRequestIdRef.current[req.request_id] = {
        ...req,
        response: null,
      };

      const request: ProxyRequest = {
        id: req.request_id,
        generatedAt: new Date(),
        description: `${req.method} ${req.path}`,
        text: formatRequestText(req),
        editedText: null,
        response: null,
      };

      setRequests(prev => {
        const updated = [...prev, request];
        return updated.sort((a, b) => b.generatedAt.getTime() - a.generatedAt.getTime());
      });

      if (isIntercepting) {
        pendingRequestIdRef.current = req.request_id;
        setPendingRequestText(request.text);
      }
    },
    [isIntercepting]
  );

  /**
   * Handle incoming response messages - extracted to useCallback for consistency
   */
  const handleResponseMessage = useCallback((ev: Event) => {
    const messageEvent = ev as MessageEvent;
    const res = JSON.parse(messageEvent.data) as ProxyResponseMessage;

    if (pendingRequestIdRef.current === res.request_id) {
      pendingRequestIdRef.current = "";
      setPendingRequestText(null);
    }

    const state = requestStatesByRequestIdRef.current[res.request_id];
    if (state === undefined || state.response !== null) {
      return;
    }

    state.response = { ...res };

    setRequests(prev => {
      const i = prev.findIndex(x => x.id === res.request_id);
      if (i >= 0) {
        const updated = [...prev];
        updated[i] = {
          ...prev[i],
          response: {
            generatedAt: parseDateHeader(res.headers) || new Date(),
            statusCode: res.status_code,
            text: formatResponseText(res),
          },
        };
        return updated.sort((a, b) => b.generatedAt.getTime() - a.generatedAt.getTime());
      }
      return prev;
    });
  }, []);

  /**
   * Initialize the proxy EventSource on mount
   */
  useEffect(() => {
    if (!url || sessionStatus !== "ready") {
      if (sourceRef.current) {
        sourceRef.current.close();
        sourceRef.current = null;
      }
      initializedForUrlRef.current = null;
      return;
    }

    // Only initialize once per URL
    if (initializedForUrlRef.current === url && sourceRef.current) {
      return;
    }

    if (sourceRef.current) {
      sourceRef.current.close();
      sourceRef.current = null;
    }

    // Initialize EventSource to receive updates
    const initializeProxy = async () => {
      initializedForUrlRef.current = url;
      await setInterceptStateInternal(false);
      sourceRef.current = new EventSource(`${url}/proxy/intercept`, { withCredentials: true });

      // Handle errors
      sourceRef.current.onerror = ev => {
        const err = ev instanceof ErrorEvent ? ev.error : new Error("proxy event stream error");
        sourceRef.current?.close();
        sourceRef.current = null;
        initializedForUrlRef.current = null;
        onError(err);
      };
    };

    initializeProxy();

    return () => {
      if (sourceRef.current) {
        sourceRef.current.close();
        sourceRef.current = null;
      }
      initializedForUrlRef.current = null;
    };
  }, [url, sessionStatus]);

  /**
   * Attach/update event listeners when handlers change
   */
  useEffect(() => {
    if (!sourceRef.current) {
      return;
    }

    // Attach current handlers
    sourceRef.current.addEventListener("request", handleRequestMessage);
    sourceRef.current.addEventListener("response", handleResponseMessage);

    // Cleanup: remove old handlers
    return () => {
      if (sourceRef.current) {
        sourceRef.current.removeEventListener("request", handleRequestMessage);
        sourceRef.current.removeEventListener("response", handleResponseMessage);
      }
    };
  }, [sourceRef.current, handleRequestMessage, handleResponseMessage]);

  const setInterceptStateInternal = useCallback(
    async (shouldIntercept: boolean): Promise<boolean> => {
      const r = await fetch(`${url}/proxy/intercept`, {
        credentials: "include",
        method: shouldIntercept ? "put" : "delete",
      });

      if (!r.ok) {
        const status = r ? r.status : -1;
        const error = new Error(`failed to set proxy intercept state: got status ${status}`);
        onError(error);
        return false;
      }

      setIsIntercepting(shouldIntercept);

      if (!shouldIntercept) {
        pendingRequestIdRef.current = "";
        setPendingRequestText(null);
      }

      return true;
    },
    [url, onError]
  );

  const resumeInterceptedRequest = useCallback(
    async (requestText: string): Promise<boolean> => {
      const payload = parseRequestText(requestText);
      if (!payload) {
        return false;
      }

      const init: RequestInit = {
        credentials: "include",
        method: "post",
        body: JSON.stringify(payload),
      };
      setHeaderValue(init, "Content-Type", "application/json");

      const r = await fetch(`${url}/proxy/resume`, init);
      if (!r.ok) {
        const status = r ? r.status : -1;
        const error = new Error(`failed to resume proxy request: got status ${status}`);
        onError(error);
        return false;
      }

      if (pendingRequestIdRef.current && pendingRequestText !== requestText) {
        setRequests(prev => {
          const i = prev.findIndex(x => x.id === pendingRequestIdRef.current);
          if (i >= 0) {
            const updated = [...prev];
            updated[i] = { ...prev[i], editedText: requestText };
            return updated.sort((a, b) => b.generatedAt.getTime() - a.generatedAt.getTime());
          }
          return prev;
        });
      }

      pendingRequestIdRef.current = "";
      setPendingRequestText(null);

      return true;
    },
    [url, onError, pendingRequestText]
  );

  return {
    isIntercepting,
    requests,
    pendingRequestText,
    setInterceptState: setInterceptStateInternal,
    resumeInterceptedRequest,
  };
};
